/* global Promise modForm Modalytics */
import {
	getCurrentStep,
	focusOnFirstErrorOnCurrentStep,
	switchFormLoadingState,
	populateFormWithZipData,
	populateLocationFieldsDynamically,
	showNextStep,
	trackInvalidFieldsInHeap,
	markFieldAsInvalid,
	getFormData,
	preprocessQSFormData,
	preprocessContractorFormData,
	submitForm,
	showStep,
	hashStepTracking,
	initFormSubmitLoaderAssets,
	saveFormDataToCookie,
	getConsentListing,
	trackCROTagToHeap
} from '../../../helpers';
import { isFormValid, isVisible, isZipCodeValid } from '../../../validators';
import { getTrestlePhoneValidityScore } from '../../requests/getTrestlePhoneValidityScore';

export async function formSubmitCallback(e) {
	e.stopImmediatePropagation();
	e.preventDefault();

	const $activeStep = getCurrentStep();

	if (modForm.opts.zipFormSelectorsToSync.length && $activeStep.getAttribute('data-step-name') === 'zip' && e.target.id !== modForm.opts.formId) {
		if (!isFormValid(e.target)) {
			const zipInput = e.target.querySelector('input[name="zip"]');
			if (zipInput) {
				zipInput.focus();
				return;
			}
		}
	}
	// Validate step fields
	if (!isFormValid($activeStep)) {
		focusOnFirstErrorOnCurrentStep();
		return;
	}

	// commenting out for p1 -- HS-9001
	// const $phoneField = $activeStep?.querySelector('input[name="homePhone"]');
	// if ($phoneField) {
	// 	switchFormLoadingState(true);
	// 	const isPhoneValid = await isValidUSPhoneNumber($phoneField.value);
	// 	switchFormLoadingState(false);
	// 	if (!isPhoneValid) {
	// 		focusOnFirstErrorOnCurrentStep();
	// 		markFieldAsInvalid($phoneField);
	// 		return;
	// 	}
	// }

	// Get form data
	let formData = getFormData();

	// Save form data on each step
	saveFormDataToCookie(formData);

	// If not last step
	if (!modForm.isCurrentStepLast()) {
		// pull the loader assets
		initFormSubmitLoaderAssets();

		// Validate zip if step contains it
		const zipFields = modForm.opts.trackZip1ChangeDynamically ? 'input[name="zip"]' : 'input[name="zip"], input[name="zip1"]';
		let $stepZipField = $activeStep.querySelectorAll(zipFields)[0];
		const editZipInput = $activeStep.querySelectorAll(modForm.zipEditInput)[0];
		if ($stepZipField && (modForm.opts.zipCode !== $stepZipField?.value)) {
			modForm.retryZipValidation = true;
		}

		if (modForm.retryZipValidation) {
			if (editZipInput && isVisible(editZipInput)) {
				$stepZipField = editZipInput;
			} else {
				$stepZipField = modForm.opts.zipField[0];
			}
		}
		if (($stepZipField && !modForm.isZipValid) || modForm.retryZipValidation) {
			switchFormLoadingState(true);
			isZipCodeValid($stepZipField.value, function(response) {
				switchFormLoadingState(false);
				// If success..
				if (response.success) {
					// Populate city & state
					if (response.data) {
						populateFormWithZipData(response.data);
						populateLocationFieldsDynamically(response.data);
						modForm.opts.zipCode = response.data.zip;

						// Send zip code to Invoca
						if (typeof dataLayer !== 'undefined' && window.Invoca) {
							window.dataLayer.push({
								zip_code: response.data.zip // eslint-disable-line camelcase
							});
						}
					}

					if ($stepZipField.name === 'zip1') {
						// If we're currently fetching live consent listing in the page, fetch it one more time with an updated zipcode
						if (modForm.opts.gettingLiveConsent) {
							getConsentListing(modForm.opts.getConsentListingCallback);
						}

						// Fire callback
						if ('function' === typeof modForm.opts.zip1FieldValidated) {
							modForm.opts.zip1FieldValidated(response.data);
						}
					} else {
						if ('function' === typeof modForm.opts.zipCodeValidated) {
							modForm.opts.zipCodeValidated(response.data);
						}
					}
					showNextStep();
				} else { // or mark field invalid on error
					trackInvalidFieldsInHeap('Zip');
					markFieldAsInvalid($stepZipField);
					focusOnFirstErrorOnCurrentStep();
				}
			});
		} else {
			showNextStep();
		}
	} else { // Submit whole form if on last step
		new Promise(function(resolve) {
			let response = {
				status: true
			};
			// validate all the steps again
			modForm.opts.steps.forEach(async function(step, index) {
				const hasExtraStep = step.querySelector('.step__extra');
				if (!step.classList.contains('skipped') && !hasExtraStep && !isFormValid(step)) {
					response = {
						status: false,
						step
					};
				}
				if (modForm.opts.steps.length - 1 === index) {
					resolve(response);
				}
			});
		}).then(async(response) => {
			if (!response.status) {
				const step = response?.step;
				hashStepTracking();
				showStep(step?.id);
				focusOnFirstErrorOnCurrentStep();
				return;
			} else {
				// Landing Page GA Conversion Intent Event
				Modalytics.gaSend({
					hitType: 'event',
					eventCategory: 'Homeowner Lead Form',
					eventAction: 'Clicked Submit',
					eventLabel: 'S3 Landing Page'
				}, modForm.opts.gaTracker);

				// Preprocess it
				if (modForm.opts.useContractorApi) {
					formData = preprocessContractorFormData(formData);
				} else {
					formData = preprocessQSFormData(formData);
				}
				trackCROTagToHeap();

				if (modForm.opts.useTrestleApiToTrackPhoneValidity && !modForm.phoneActivityScore) {
					switchFormLoadingState(true);
					const phoneField = document.querySelector('[name="homePhone"]');
					if (modForm.trestlePhoneValidationPromise && modForm.checkingActivityScoreForPhoneNumber === phoneField.value) {
						await modForm.trestlePhoneValidationPromise;
					} else if (phoneField) {
						await getTrestlePhoneValidityScore(phoneField.value);
					}
					switchFormLoadingState(false);
					if (modForm.phoneActivityScore) {
						if (modForm.phoneActivityScore <= modForm.opts.trestlePhoneValidityMinActivityScore) {
							markFieldAsInvalid(phoneField);
							return;
						}
					}
				}
				if (modForm.opts.beforeSubmitAsyncCallback && 'function' === typeof modForm.opts.beforeSubmitAsyncCallback) {
					formData = await modForm.opts.beforeSubmitAsyncCallback(formData).catch(() => formData);
				}

				submitForm(formData);
			}
		});
	}
}
